import React, { useEffect, useState } from "react";
import {
  Form, Input, Button, DatePicker, InputNumber, Upload, message,
  Space, Divider, Image, Popconfirm, Row, Col, Alert,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import useApi from "../../../hooks/useApi";
import { API_METHODS } from "../../../constants";
import { isEmpty } from "../../../generalFunctions";

const { TextArea } = Input;
const { RangePicker } = DatePicker;

function EventForm({ editingEvent, onCreated, onUpdated, onCancel }) {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [fileList, setFileList] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [slugPreview, setSlugPreview] = useState("");

  const isEdit = !!editingEvent;

  const [createResponse, createRequest] = useApi("/admin/events", null, API_METHODS.POST, true);
  const [updateResponse, updateRequest] = useApi(null, null, API_METHODS.PUT, true);
  const [deleteImgResponse, deleteImgRequest] = useApi(null, null, API_METHODS.DELETE);
  const [detailResponse, detailRequest] = useApi(null, null, API_METHODS.GET);

  // Load event details when editing
  useEffect(() => {
    if (isEdit && editingEvent?.id) {
      detailRequest(null, API_METHODS.GET, `/admin/events/${editingEvent.id}`);
    } else {
      form.resetFields();
      setFileList([]);
      setExistingImages([]);
      setSlugPreview("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingEvent]);

  useEffect(() => {
    if (!isEmpty(detailResponse) && !detailResponse.isLoading && detailResponse.data) {
      const ev = detailResponse.data;
      form.setFieldsValue({
        heading: ev.heading,
        sub_heading: ev.sub_heading || "",
        description: ev.description || "",
        dateRange: [dayjs(ev.start_date), dayjs(ev.end_date)],
        price_single: ev.price_single || null,
        price_couple: ev.price_couple || null,
        price_family: ev.price_family || null,
      });
      setExistingImages(ev.images || []);
      setFileList([]);
    }
  }, [detailResponse, form]);

  // Slug preview from heading + start date
  const updateSlugPreview = (heading, startDate) => {
    if (!heading || !startDate) { setSlugPreview(""); return; }
    const base = heading
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .slice(0, 50);
    const dateStr = dayjs(startDate).format("YYYY-MM-DD");
    setSlugPreview(`/event/${base}-${dateStr}`);
  };

  const handleFormValuesChange = (_, allValues) => {
    const heading = allValues.heading || "";
    const start = allValues.dateRange?.[0];
    if (!isEdit) updateSlugPreview(heading, start);
  };

  // Create response
  useEffect(() => {
    if (!isEmpty(createResponse) && !createResponse.isLoading) {
      if (createResponse.data) {
        messageApi.success("Event created successfully!");
        form.resetFields();
        setFileList([]);
        setSlugPreview("");
        onCreated?.();
      }
      if (createResponse.error) messageApi.error(createResponse.error);
    }
  }, [createResponse, messageApi, form, onCreated]);

  // Update response
  useEffect(() => {
    if (!isEmpty(updateResponse) && !updateResponse.isLoading) {
      if (updateResponse.data) {
        messageApi.success("Event updated successfully!");
        onUpdated?.();
      }
      if (updateResponse.error) messageApi.error(updateResponse.error);
    }
  }, [updateResponse, messageApi, onUpdated]);

  // Delete image response
  useEffect(() => {
    if (!isEmpty(deleteImgResponse) && !deleteImgResponse.isLoading) {
      if (deleteImgResponse.data) {
        messageApi.success("Image deleted");
      }
      if (deleteImgResponse.error) messageApi.error(deleteImgResponse.error);
    }
  }, [deleteImgResponse, messageApi]);

  const handleDeleteExistingImage = (imageId) => {
    deleteImgRequest(null, API_METHODS.DELETE, `/admin/events/${editingEvent.id}/images/${imageId}`);
    setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const totalImages = existingImages.length + fileList.length;
      if (totalImages > 4) {
        messageApi.error("Maximum 4 images allowed in total");
        return;
      }

      const [startDate, endDate] = values.dateRange;
      const formData = new FormData();
      formData.append("heading", values.heading);
      formData.append("sub_heading", values.sub_heading || "");
      formData.append("description", values.description || "");
      formData.append("start_date", startDate.format("YYYY-MM-DD"));
      formData.append("end_date", endDate.format("YYYY-MM-DD"));
      if (values.price_single) formData.append("price_single", values.price_single);
      if (values.price_couple) formData.append("price_couple", values.price_couple);
      if (values.price_family) formData.append("price_family", values.price_family);

      fileList.forEach((f) => {
        if (f.originFileObj) formData.append("images", f.originFileObj);
      });

      if (isEdit) {
        updateRequest(formData, API_METHODS.PUT, `/admin/events/${editingEvent.id}`);
      } else {
        createRequest(formData);
      }
    });
  };

  const uploadProps = {
    listType: "picture-card",
    fileList,
    beforeUpload: () => false, // prevent auto-upload
    onChange: ({ fileList: newList }) => {
      const maxNew = 4 - existingImages.length;
      setFileList(newList.slice(0, maxNew));
    },
    accept: "image/*",
    multiple: true,
  };

  const isLoading = createResponse?.isLoading || updateResponse?.isLoading;

  return (
    <>
      {contextHolder}
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleFormValuesChange}
        style={{ maxWidth: 700 }}
      >
        <Form.Item label="Heading" name="heading" rules={[{ required: true, message: "Required" }]}>
          <Input placeholder="e.g. Navratri Special Puja 2026" />
        </Form.Item>

        <Form.Item label="Sub Heading" name="sub_heading">
          <Input placeholder="e.g. Celebrate the 9 nights of Goddess" />
        </Form.Item>

        <Form.Item label="Detailed Information" name="description">
          <TextArea rows={5} placeholder="Describe the event, rituals, what's included..." />
        </Form.Item>

        <Form.Item
          label="Event Date Range"
          name="dateRange"
          rules={[{ required: true, message: "Required" }]}
        >
          <RangePicker style={{ width: "100%" }} />
        </Form.Item>

        {!isEdit && slugPreview && (
          <Alert
            message={<span>Event URL: <strong>{window.location.origin.replace("admin.", "")}{slugPreview}</strong></span>}
            type="info"
            style={{ marginBottom: 16 }}
          />
        )}

        <Divider>Packages (set at least one price)</Divider>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Single (₹)" name="price_single">
              <InputNumber style={{ width: "100%" }} min={1} placeholder="e.g. 501" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Couple (₹)" name="price_couple">
              <InputNumber style={{ width: "100%" }} min={1} placeholder="e.g. 1001" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Family (₹)" name="price_family">
              <InputNumber style={{ width: "100%" }} min={1} placeholder="e.g. 1501" />
            </Form.Item>
          </Col>
        </Row>

        <Divider>Images (max 4)</Divider>

        {/* Existing images (edit mode) */}
        {existingImages.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, color: "#666" }}>Current images:</div>
            <Space wrap>
              {existingImages.map((img) => (
                <div key={img.id} style={{ position: "relative" }}>
                  <Image
                    src={img.image_url}
                    width={100}
                    height={100}
                    style={{ objectFit: "cover", borderRadius: 8 }}
                  />
                  <Popconfirm
                    title="Delete this image?"
                    onConfirm={() => handleDeleteExistingImage(img.id)}
                  >
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      style={{ position: "absolute", top: 2, right: 2 }}
                    />
                  </Popconfirm>
                </div>
              ))}
            </Space>
          </div>
        )}

        <Upload {...uploadProps}>
          {existingImages.length + fileList.length < 4 && (
            <div>
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          )}
        </Upload>
        <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
          {existingImages.length + fileList.length}/4 images
        </div>

        <div style={{ marginTop: 24 }}>
          <Space>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={isLoading}
              style={{ background: "linear-gradient(to right, #f97316, #ef4444)", border: "none" }}
            >
              {isEdit ? "Update Event" : "Create Event"}
            </Button>
            {isEdit && (
              <Button onClick={onCancel}>Cancel</Button>
            )}
          </Space>
        </div>
      </Form>
    </>
  );
}

export default EventForm;
